import React from 'react'
import { useMutation } from '@apollo/client'
import { Link, useLocation } from 'react-router-dom'
import { CreatorCardIncomplete } from '@winnin/winnin_ui.components.creator-card-incomplete'
import { useToast } from '@winnin/winnin_ui.components.toast'

import creatorThumbnail from 'assets/images/svg/creator-added-thumbnail.svg'
import Thumbnail from 'components/Thumbnail'
import useHover from 'hooks/useHover'
import { useTranslate, T } from 'lib/tolgee'
import { analyticsTracker, TOLGEE_GLOBALS } from 'utils'

import CreatorLink from './CreatorLink'
import { ADD_CRETOR_REQUEST_MUTATION } from './query'
import CreatorCardFetcher from '../CreatorCardFetcher'
import './index.scss'

const INSERT_BASE_URL = {
  yt: 'https://www.youtube.com',
  fb: 'https://www.facebook.com',
  in: 'https://www.instagram.com',
  tt: 'https://www.tiktok.com',
  tw: 'https://www.twitch.tv',
}
const TOLGEE_PREFIX = 'CreatorCardOnHover'

const CreatorCardOnHover = ({
  title,
  id,
  provider,
  groupId,
  type,
  noLink,
  noHover,
  videoTitle,
  onCreatorClickCallback,
}) => {
  const { createToast } = useToast()
  const t = useTranslate()
  const location = useLocation()
  const [hoverRef, isHovered] = useHover()

  const getPage = () => {
    if (location.pathname.includes('/post/')) {
      return 'Video page'
    }
    if (location.pathname.includes('/search/video')) {
      return 'Search videos page'
    }
    return null
  }

  const isIncompleteCreator = type?.startsWith('CreatorIncomplete')
  const shouldRenderPopover = !isIncompleteCreator && isHovered && !noHover
  const shouldRenderInsertPopover = isIncompleteCreator && isHovered && !noHover

  const [createCreatorRequest, { loading }] = useMutation(ADD_CRETOR_REQUEST_MUTATION, {
    onCompleted({ createCreatorRequest }) {
      const pageName = getPage()
      const { INSERT_CREATOR } = analyticsTracker.eventNames
      const {
        success,
        message,
        title,
        creatorId,
        groupId,
        name,
        thumbnail,
        provider: responseProvider,
      } = createCreatorRequest
      if (success === true && message === 'CREATOR_INSERT_PROCESS_STARTED') {
        createToast({
          content: (
            <div className="CreatorCardOnHover-InsertCreator-Toast">
              <Thumbnail alt="thumbnail-default" provider={provider} src={creatorThumbnail} />

              <div className="toast-message">
                <p>
                  <T keyName={`${TOLGEE_PREFIX}-CreatorInsertSuccessMessage`}>
                    The creator will be added to our database in about an hour
                  </T>
                </p>
              </div>
            </div>
          ),
        })
      }
      if (success === true && message === 'CREATOR_UPDATE_PROCESS_STARTED') {
        createToast({
          content: (
            <div className="CreatorCardOnHover-UpdateCreator-Toast">
              <Thumbnail alt="thumbnail-default" provider={provider} src={creatorThumbnail} />

              <div className="toast-message">
                <p>
                  <T keyName={`${TOLGEE_PREFIX}-CreatorUpdateSuccessMessage`}>
                    The creator will be updated in our database in about an hour
                  </T>
                </p>
              </div>
            </div>
          ),
        })
      }
      if (message === 'INSERTION_REQUEST_ALREADY_EXISTS') {
        createToast({
          content: (
            <p>
              <T keyName={`${TOLGEE_PREFIX}-CreatorInsertProcessingMessage`}>
                The requested creator is already being processed in our database.
              </T>
            </p>
          ),
        })
      }
      if (creatorId) {
        const redirectUrl = `/group/${groupId}/${creatorId}/${responseProvider}/overview`
        createToast({
          content: (
            <div className="CreatorCardOnHover-InsertCreator-Toast">
              <Thumbnail alt="thumbnail-default" provider={responseProvider} src={thumbnail ?? ''} />
              <div className="toast-message">
                <p>
                  <T
                    keyName={`${TOLGEE_PREFIX}-CreatorInsertAlreadyExistMessage`}
                    parameters={{
                      span: <span className="toast-highlight" />,
                      creatorName: name,
                    }}
                  >
                    {
                      'Creator <span>{creatorName}</span> was already in our database. The creator will be updated in about an hour.'
                    }
                  </T>
                </p>
                <Link className="toast-link" to={redirectUrl}>
                  <T keyName={`${TOLGEE_PREFIX}-toastExistingCreatorLink`}>Click here to access the creators’ page</T>
                </Link>
              </div>
            </div>
          ),
        })
      }
      if (message === 'CREATOR_NOT_FOUND') {
        createToast({
          content: (
            <p>
              <T keyName={`${TOLGEE_PREFIX}-CreatorInsertNotFoundMessage`}>
                The requested creator was not found, please get in touch with our CS team.
              </T>
            </p>
          ),
        })
      }
      analyticsTracker.eventTrack(INSERT_CREATOR.CLICK, {
        ...(pageName && { 'Page Name': pageName }),
        'Creator Name': name,
        'Video Title': videoTitle,
        Status: (title || message) ?? 'CREATOR_ALREADY_EXISTS',
      })
    },
    onError(error) {
      console.error('InsertCreator @ createCreatorRequest >>>>>', error)

      createToast({
        content: t(TOLGEE_GLOBALS.defaultError),
      })
    },
  })

  const handleInsertClick = provider => {
    if (loading) {
      return
    }

    let parsedPath
    if (provider === 'tt') {
      parsedPath = `@${title}`
    } else if (provider === 'yt') {
      parsedPath = `channel/${id}`
    } else {
      parsedPath = title
    }

    return createCreatorRequest({
      variables: {
        data: {
          url: `${INSERT_BASE_URL[provider]}/${parsedPath}`,
        },
      },
    })
  }

  return (
    <div ref={hoverRef} className="CreatorCardOnHover">
      {shouldRenderPopover && (
        <div className="CreatorCardOnHover-popover">
          <div className="CreatorCardOnHover-popoverContent CreatorCardOnHover-popoverContent-withPadding">
            <CreatorCardFetcher id={id} provider={provider} />
          </div>
        </div>
      )}

      {shouldRenderInsertPopover && (
        <div
          className="CreatorCardOnHover-popover CreatorCardOnHover-popover-insert"
          onClick={e => {
            e.stopPropagation()
          }}
        >
          <CreatorCardIncomplete
            creatorName={title}
            onClick={() => handleInsertClick(provider)}
            provider={provider}
            message={t({
              defaultValue: 'New creator! Click below to insert creator in our database.',
              key: `${TOLGEE_PREFIX}-creatorInsertText`,
            })}
          />
        </div>
      )}

      <CreatorLink
        onCreatorClickCallback={onCreatorClickCallback}
        noLink={noLink}
        isIncompleteCreator={isIncompleteCreator}
        title={title}
        id={id}
        provider={provider}
        groupId={groupId}
      />
    </div>
  )
}

export default CreatorCardOnHover
